import { Box } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { HOME_LAYOUT_HEIGHT } from "../../app/common";
import { useAppSelector } from "../../app/hooks";
import {
  FilterSearchField,
  SearchFilter,
} from "../../common/components/functional/FilterSearchField/FilterSearchField";
import { Heading1 } from "../../common/components/styled/styled";
import {
  TYPE_EMPLOYER_HEALTH_INSURANCE_REDUCTION,
  TYPE_PAYSLIP,
  TYPE_WAGE_REPORT,
} from "./constants";
import { EmployerHealthInsuranceReductions } from "./employerHealthInsuranceReduction/EmployerHealthInsuranceReductions";
import { MainFilters } from "./MainFilters";
import { SalaryPagePayslips } from "./payslips/SalaryPagePayslips";
import { WageReports } from "./wageReports/WageReports";
import { mergeInitialAndStoredFilters } from "../../common/helpers/filters";

export type ModalState = { open: boolean; id?: string };

export const SalariesPage = () => {
  const translationNs = "payslips";
  const { t } = useTranslation(translationNs);
  const { status } = useAppSelector((state) => state.payslips);
  const isLoading = status === "pending";

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const defaultFilters: SearchFilter[] = [
    { name: "type", value: "" },
    { name: "salaryDateFrom", value: "", type: "date" },
    { name: "salaryDateTo", value: "", type: "date" },
    { name: "valueDateFrom", value: "", type: "date" },
    { name: "valueDateTo", value: "", type: "date" },
    { name: "incomeNetFrom", value: "" },
    { name: "incomeNetTo", value: "" },
    { name: "eezyCompanyIds", value: [""] },
    { name: "talentIds", value: "" },
    { name: "ssn", value: "" },
    { name: "salaryPeriodClassId", value: "" },
    { name: "statues", value: "" },
    { name: "statusCodes", value: [] },
    { name: "isManual", value: "" },
    { name: "salaryTypes", value: [] },
    // employer health insurance reduction filters
    { name: "employerId", value: "" },
    { name: "employerCostCenterId", value: "" },
    { name: "customerOrganizationId", value: "" },
    { name: "yearMonth", value: "" },
  ];
  const SALARY_FILTERS_LOCAL_STORAGE = "salaryFilters";
  const fromLocalStorage = localStorage.getItem(SALARY_FILTERS_LOCAL_STORAGE);
  const userFilters: SearchFilter[] =
    fromLocalStorage && JSON.parse(fromLocalStorage);

  // "statusCodes" have been changed from string to array so
  // need to account for old existing user filters
  // TODO delete later?
  const sanitizedUserFilters: SearchFilter[] = userFilters?.map((filter) => {
    if (filter.name === "statusCodes" && !Array.isArray(filter.value)) {
      return {
        name: "statusCodes",
        value: [filter.value],
        displayValue: filter.displayValue,
      };
    } else {
      return filter;
    }
  });

  const [filters, setFilters] = useState<SearchFilter[]>(
    sanitizedUserFilters
      ? // add filters introduced after the user's filters were stored
        mergeInitialAndStoredFilters(defaultFilters, sanitizedUserFilters)
      : defaultFilters,
  );
  const type = filters.find((filter) => filter.name === "type");
  const isPayslipsView = useMemo(() => type?.value === TYPE_PAYSLIP, [type]);
  const isWageReportsView = useMemo(
    () => type?.value === TYPE_WAGE_REPORT,
    [type],
  );
  const isReductionsView = useMemo(
    () => type?.value === TYPE_EMPLOYER_HEALTH_INSURANCE_REDUCTION,
    [type],
  );

  const handleFilterMenuOpenClose = () => {
    setIsFilterMenuOpen(!isFilterMenuOpen);
  };

  const updateFilters = (filters: SearchFilter[]) => {
    setFilters(filters);
    localStorage.setItem(SALARY_FILTERS_LOCAL_STORAGE, JSON.stringify(filters));
  };

  return (
    <Box
      data-testid="salaries-page-root"
      height={HOME_LAYOUT_HEIGHT}
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Heading1>{t("payslipsWageReportsAndReductions")}</Heading1>
      <FilterSearchField
        width="440px"
        isOpen={isFilterMenuOpen}
        toggleMenu={handleFilterMenuOpenClose}
        label={t("common:search") || ""}
        value={""}
        onChange={() => {}}
        hideSearchHistory={true}
        searchHistory={false}
        selectFromHistory={() => {}}
        translation={translationNs}
        filters={filters}
        updateFilters={updateFilters}
        isLoading={isLoading}
        filtersLocalStorageName={SALARY_FILTERS_LOCAL_STORAGE}
        defaultFilters={defaultFilters}
        hideReturnDefaultFilters
      >
        <MainFilters
          filters={filters}
          updateFilters={updateFilters}
          handleMenuToggle={handleFilterMenuOpenClose}
        />
      </FilterSearchField>

      <Box flex={1} minHeight={0} overflow="hidden">
        {isPayslipsView ? (
          <SalaryPagePayslips filters={filters} />
        ) : isWageReportsView ? (
          <WageReports filters={filters} />
        ) : isReductionsView ? (
          <EmployerHealthInsuranceReductions filters={filters} />
        ) : null}
      </Box>
    </Box>
  );
};
